import React, { useEffect, useRef, useState } from 'react';

const useCountUp = (end, duration = 2000, startOnView = false) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasStarted, startOnView]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [hasStarted, end, duration]);

  return { count, ref };
};

const StatCard = ({ value, suffix, label, prefix = '' }) => {
  const numericValue = parseInt(value, 10);
  const { count, ref } = useCountUp(numericValue, 2000, true);

  return (
    <div
      ref={ref}
      className="gsap-reveal text-center p-8 rounded-2xl bg-white dark:bg-dark-bg border border-brown/10 dark:border-burnt/10 hover:border-burnt/30 transition-all duration-500 hover:shadow-premium dark:hover:shadow-premium-dark"
    >
      <p className="text-4xl md:text-5xl font-heading font-bold text-burnt mb-2">
        {prefix}{count}{suffix}
      </p>
      <p className="text-dark/70 dark:text-cream/70 font-body">{label}</p>
    </div>
  );
};

const Resultados = () => {
  const resultados = [
    { value: '15', suffix: '+', label: 'Projetos entregues' },
    { value: '100', suffix: '%', label: 'Sites responsivos' },
    { value: '24', suffix: 'h', label: 'Tempo de resposta' },
    { value: '5', suffix: '', label: 'Estrelas de avaliação' },
  ];

  const beneficios = [
    'Mais visibilidade no Google e redes sociais',
    'Credibilidade profissional desde o primeiro clique',
    'Atendimento humanizado e suporte direto comigo',
    'Soluções personalizadas para cada negócio',
    'Sites rápidos, seguros e otimizados para SEO',
    'Presença digital que gera oportunidades reais',
  ];

  return (
    <section
      id="resultados"
      className="py-32 bg-cream dark:bg-dark-bg transition-colors duration-500"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 gsap-reveal">
          <p className="text-burnt font-heading font-semibold uppercase tracking-wider text-sm mb-4">
            Resultados
          </p>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark dark:text-cream mb-4">
            Benefícios que transformam negócios
          </h2>
          <p className="text-xl text-dark/70 dark:text-cream/70 max-w-2xl mx-auto font-body">
            Cada site que desenvolvo é pensado para gerar resultados concretos para o seu negócio.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {resultados.map((item, index) => (
            <StatCard key={index} {...item} />
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {beneficios.map((beneficio, index) => (
            <div
              key={index}
              className="gsap-reveal flex items-start gap-3 p-4 rounded-xl hover:bg-white/50 dark:hover:bg-dark-surface/50 transition-colors duration-300"
            >
              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-burnt mt-2" />
              <p className="text-dark/80 dark:text-cream/80 font-body">{beneficio}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Resultados;
